import { Controller } from "stimulus"
import { loadEmailSuggestions, rankEmailSuggestions, rememberEmail, Suggestion } from "../utils/email_suggestions"

const EMAIL = /^[^@\s,]+@[^@\s,]+\.[^@\s,]+$/

export default class extends Controller {
  static targets = ["select", "chips", "input", "menu"]
  static values = { suggestionsUrl: String }

  readonly selectTarget!: HTMLSelectElement
  readonly chipsTarget!: HTMLElement
  readonly inputTarget!: HTMLInputElement
  readonly menuTarget!: HTMLElement
  readonly hasMenuTarget!: boolean
  readonly suggestionsUrlValue!: string

  private observer?: MutationObserver
  private suggestions: Suggestion[] = []
  private active = -1
  private request = 0

  connect(): void {
    this.render()

    // auto_save_controller writes data-previous-value after every settled save, and rewrites the
    // option selection first when the save failed. Repainting on that attribute keeps the chips
    // honest without this controller having to know anything about saving.
    this.observer = new MutationObserver(() => this.render())
    this.observer.observe(this.selectTarget, { attributes: true, attributeFilter: ["data-previous-value"] })
  }

  disconnect(): void {
    this.observer?.disconnect()
    this.closeMenu()
  }

  suggest(): void {
    if (!this.suggestionsUrlValue || !this.hasMenuTarget) return

    const query = this.inputTarget.value
    const request = ++this.request
    loadEmailSuggestions(this.suggestionsUrlValue).then(emails => {
      if (request !== this.request) return // a later keystroke owns the menu
      this.suggestions = rankEmailSuggestions(query, emails, this.values())
      // a finished address keeps Enter for itself; a partial one gets the top suggestion highlighted
      this.active = this.suggestions.length && !this.valid(query.trim().toLowerCase()) ? 0 : -1
      this.renderMenu()
    })
  }

  pickSuggestion(event: MouseEvent): void {
    // mousedown, so the input keeps focus and its blur doesn't commit the half-typed text first
    event.preventDefault()
    const email = (event.currentTarget as HTMLElement).dataset.email
    if (email) this.choose(email)
  }

  focusInput(event: Event): void {
    if ((event.target as HTMLElement).closest(".chip-input-remove")) return
    if (this.inputTarget.disabled) return

    this.inputTarget.focus()
  }

  keydown(event: KeyboardEvent): void {
    if (this.menuOpen()) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault()
        const step = event.key === "ArrowDown" ? 1 : -1
        this.active = (this.active + step + this.suggestions.length) % this.suggestions.length
        this.renderMenu()
        return
      }
      if (event.key === "Escape") {
        event.preventDefault()
        event.stopPropagation()
        this.closeMenu()
        return
      }
      if ((event.key === "Enter" || event.key === "Tab") && this.active >= 0) {
        event.preventDefault()
        this.choose(this.suggestions[this.active].email)
        return
      }
    }

    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault()
      this.commit(this.inputTarget.value)
    } else if (event.key === "Backspace" && this.inputTarget.value === "") {
      this.removeLast()
    }
  }

  commitOnBlur(): void {
    this.closeMenu()

    // A blur fires before the click that caused it, so committing a blank input here would
    // swallow a click on a chip's remove button.
    if (this.inputTarget.value.trim() === "") return

    this.commit(this.inputTarget.value)
  }

  paste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData("text") || ""
    if (!pasted.includes(",")) return

    event.preventDefault()
    this.commit(pasted)
  }

  clearInvalid(): void {
    this.inputTarget.classList.remove("is-invalid")
  }

  remove(event: Event): void {
    event.preventDefault()
    event.stopPropagation()

    const address = (event.currentTarget as HTMLElement).dataset.address
    if (!address) return

    this.optionFor(address)!.selected = false
    this.render()
    this.notify()
  }

  // --- internals ---------------------------------------------------------

  private commit(raw: string): void {
    const entries = raw.split(",").map(entry => entry.trim()).filter(entry => entry !== "")
    if (entries.length === 0) return

    const invalid: string[] = []
    let added = false

    entries.forEach(entry => {
      const address = entry.toLowerCase()
      if (!this.valid(address)) {
        invalid.push(entry)
        return
      }
      if (this.values().includes(address)) return

      this.select(address)
      added = true
    })

    this.inputTarget.value = invalid.join(", ")
    this.inputTarget.classList.toggle("is-invalid", invalid.length > 0)
    this.closeMenu()

    if (added) {
      this.render()
      this.notify()
    }
  }

  private choose(email: string): void {
    this.inputTarget.value = ""
    this.commit(email)
    if (this.suggestionsUrlValue) rememberEmail(this.suggestionsUrlValue, email)
    this.inputTarget.focus()
  }

  private menuOpen(): boolean {
    return this.hasMenuTarget && !this.menuTarget.hidden && this.suggestions.length > 0
  }

  private closeMenu(): void {
    if (!this.hasMenuTarget) return
    this.suggestions = []
    this.active = -1
    this.menuTarget.hidden = true
    this.menuTarget.innerHTML = ""
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.inputTarget.removeAttribute("aria-activedescendant")
  }

  private renderMenu(): void {
    if (!this.suggestions.length) {
      this.closeMenu()
      return
    }

    const query = this.inputTarget.value.trim().toLowerCase()
    this.menuTarget.innerHTML = ""
    this.suggestions.forEach((suggestion, index) => {
      const option = document.createElement("div")
      option.className = "chip-input-suggest-opt" + (index === this.active ? " is-active" : "")
      option.id = `${this.selectTarget.id || "chip"}-suggest-${index}`
      option.setAttribute("role", "option")
      option.setAttribute("aria-selected", String(index === this.active))
      option.dataset.email = suggestion.email
      option.dataset.action = "mousedown->email-chips#pickSuggestion"

      const { email, at } = suggestion
      option.append(email.slice(0, at))
      const mark = document.createElement("mark")
      mark.textContent = email.slice(at, at + query.length)
      option.append(mark, email.slice(at + query.length))
      this.menuTarget.appendChild(option)
    })

    this.menuTarget.hidden = false
    this.inputTarget.setAttribute("aria-expanded", "true")
    this.inputTarget.setAttribute("aria-controls", this.menuTarget.id || "")
    const current = this.menuTarget.children[this.active] as HTMLElement | undefined
    if (current) this.inputTarget.setAttribute("aria-activedescendant", current.id)
  }

  private removeLast(): void {
    const last = this.values().pop()
    if (!last) return

    this.optionFor(last)!.selected = false
    this.render()
    this.notify()
  }

  private select(address: string): void {
    const existing = this.optionFor(address)
    if (existing) {
      existing.selected = true
      return
    }

    const option = document.createElement("option")
    option.value = address
    option.text = address
    option.selected = true
    this.selectTarget.appendChild(option)
  }

  private optionFor(address: string): HTMLOptionElement | undefined {
    return Array.from(this.selectTarget.options).find(option => option.value === address)
  }

  private values(): string[] {
    return Array.from(this.selectTarget.selectedOptions).map(option => option.value)
  }

  private valid(address: string): boolean {
    return EMAIL.test(address)
  }

  private render(): void {
    this.chipsTarget.innerHTML = ""

    this.values().forEach(address => {
      const chip = document.createElement("span")
      chip.className = "chip-input-chip"

      const label = document.createElement("span")
      label.textContent = address
      chip.appendChild(label)

      if (!this.inputTarget.disabled) {
        const remove = document.createElement("button")
        remove.type = "button"
        remove.className = "chip-input-remove"
        remove.dataset.address = address
        remove.dataset.action = "email-chips#remove"
        remove.setAttribute("aria-label", `Remove ${address}`)
        remove.textContent = "×"
        chip.appendChild(remove)
      }

      this.chipsTarget.appendChild(chip)
    })
  }

  private notify(): void {
    this.selectTarget.dispatchEvent(new Event("change", { bubbles: true }))
  }
}
